import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Eye, Trophy, Timer, Zap } from 'lucide-react';

const ReactionTest = ({ onGameComplete, gameConfig }) => {
  const [gameState, setGameState] = useState('waiting'); // waiting, ready, active, clicked, finished
  const [score, setScore] = useState(0);
  const [round, setRound] = useState(0);
  const [reactionTimes, setReactionTimes] = useState([]);
  const [currentStartTime, setCurrentStartTime] = useState(null);
  const [timeLeft, setTimeLeft] = useState(gameConfig.duration);
  const [waitTime, setWaitTime] = useState(0);
  const [tooEarly, setTooEarly] = useState(false);
  
  const totalRounds = 8; // Total number of reaction tests
  
  // Start game
  const startGame = () => {
    setGameState('playing');
    setScore(0);
    setRound(0);
    setReactionTimes([]);
    setTimeLeft(gameConfig.duration);
    setTooEarly(false);
    startNewRound();
  };
  
  // Start a new reaction round
  const startNewRound = () => {
    setGameState('ready');
    setCurrentStartTime(null);
    setTooEarly(false);
    
    // Random wait time between 1-4 seconds
    const wait = 1000 + Math.random() * 3000;
    setWaitTime(wait);
    
    setTimeout(() => {
      if (gameState !== 'finished') {
        setGameState('active');
        setCurrentStartTime(Date.now());
      }
    }, wait);
  };
  
  // Game timer
  useEffect(() => {
    if (gameState === 'playing' || gameState === 'ready' || gameState === 'active') {
      if (timeLeft > 0) {
        const timer = setTimeout(() => {
          setTimeLeft(time => time - 1);
        }, 1000);
        return () => clearTimeout(timer);
      } else {
        endGame();
      }
    }
  }, [gameState, timeLeft]);
  
  // Handle click/tap
  const handleClick = () => {
    if (gameState === 'ready') {
      // Clicked too early
      setTooEarly(true);
      setGameState('clicked');
      
      setTimeout(() => {
        if (round + 1 >= totalRounds || timeLeft <= 3) {
          endGame();
        } else {
          setRound(prev => prev + 1);
          startNewRound();
        }
      }, 2000);
      
    } else if (gameState === 'active' && currentStartTime) {
      // Valid reaction
      const reactionTime = Date.now() - currentStartTime;
      setReactionTimes(prev => [...prev, reactionTime]);
      
      // Calculate score based on reaction time
      let roundScore = 0;
      if (reactionTime < 200) roundScore = 200; // Excellent
      else if (reactionTime < 300) roundScore = 150; // Great
      else if (reactionTime < 400) roundScore = 100; // Good
      else if (reactionTime < 500) roundScore = 75;  // Average
      else if (reactionTime < 600) roundScore = 50;  // Slow
      else roundScore = 25; // Very slow
      
      setScore(prev => prev + roundScore);
      setGameState('clicked');
      
      setTimeout(() => {
        if (round + 1 >= totalRounds || timeLeft <= 3) {
          endGame();
        } else {
          setRound(prev => prev + 1);
          startNewRound();
        }
      }, 2000);
    }
  };
  
  // End game
  const endGame = () => {
    setGameState('finished');
    
    const validReactions = reactionTimes.filter(time => time > 0);
    const averageTime = validReactions.length > 0 ? 
      validReactions.reduce((a, b) => a + b, 0) / validReactions.length : 0;
    const bestTime = validReactions.length > 0 ? Math.min(...validReactions) : 0;
    
    const gameData = {
      roundsCompleted: validReactions.length,
      averageReactionTime: Math.round(averageTime),
      bestReactionTime: Math.round(bestTime),
      finalScore: score
    };
    
    setTimeout(() => {
      onGameComplete(score, gameData);
    }, 3000);
  };
  
  if (gameState === 'waiting') {
    return (
      <div className="text-center">
        <Card className="gaming-card max-w-lg mx-auto p-8">
          <CardHeader className="pb-6">
            <Eye className="w-16 h-16 text-green-400 mx-auto mb-4" />
            <CardTitle className="text-2xl text-white">Reaction Master</CardTitle>
            <p className="text-gray-400">
              Test your reflexes! Click as soon as the screen turns green. Don't click too early!
            </p>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 mb-6">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Duration:</span>
                <span className="text-white">{gameConfig.duration} seconds</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Rounds:</span>
                <span className="text-white">{totalRounds} reaction tests</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Scoring:</span>
                <span className="text-white">Faster reactions = higher score</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Entry Fee:</span>
                <span className="text-yellow-400 font-semibold">₹10</span>
              </div>
            </div>
            <Button onClick={startGame} className="w-full btn-primary py-3 text-lg">
              Start Test
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  if (gameState === 'finished') {
    const validReactions = reactionTimes.filter(time => time > 0);
    const averageTime = validReactions.length > 0 ? 
      validReactions.reduce((a, b) => a + b, 0) / validReactions.length : 0;
    const bestTime = validReactions.length > 0 ? Math.min(...validReactions) : 0;
    
    return (
      <div className="text-center">
        <Card className="gaming-card max-w-lg mx-auto p-8">
          <CardHeader className="pb-6">
            <Trophy className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
            <CardTitle className="text-2xl text-white">Reaction Test Complete!</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 mb-6">
              <div className="text-center">
                <div className="text-4xl font-bold text-yellow-400 mb-2">{score}</div>
                <div className="text-gray-400">Final Score</div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-green-400">{Math.round(averageTime)}ms</div>
                  <div className="text-gray-400 text-sm">Average Time</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-blue-400">{Math.round(bestTime)}ms</div>
                  <div className="text-gray-400 text-sm">Best Time</div>
                </div>
              </div>
              
              <div className="text-center">
                <div className="text-lg font-bold text-purple-400">{validReactions.length}/{totalRounds}</div>
                <div className="text-gray-400 text-sm">Valid Reactions</div>
              </div>
            </div>
            
            <div className="text-gray-400 text-sm">
              {averageTime < 250 ? 'Lightning fast reflexes!' : 
               averageTime < 350 ? 'Great reaction time!' : 
               averageTime < 450 ? 'Good reflexes!' : 'Keep practicing!'}<br/>
              Returning to games lobby in 3 seconds...
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Get background color based on game state
  const getBackgroundColor = () => {
    if (gameState === 'ready') return 'bg-red-600';
    if (gameState === 'active') return 'bg-green-500';
    if (gameState === 'clicked') {
      if (tooEarly) return 'bg-red-800';
      return 'bg-blue-600';
    }
    return 'bg-gray-800';
  };
  
  // Get instruction text
  const getInstructionText = () => {
    if (gameState === 'ready') return 'Wait for green...';
    if (gameState === 'active') return 'CLICK NOW!';
    if (gameState === 'clicked') {
      if (tooEarly) return 'Too early! Wait for green.';
      const lastTime = reactionTimes[reactionTimes.length - 1];
      return `${lastTime}ms - ${lastTime < 200 ? 'Excellent!' : lastTime < 300 ? 'Great!' : lastTime < 400 ? 'Good!' : 'Not bad!'}`;
    }
    return '';
  };
  
  return (
    <div className="max-w-2xl mx-auto">
      {/* Game HUD */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex space-x-4">
          <Badge className="bg-yellow-400/20 text-yellow-400 border-yellow-400/30 px-4 py-2">
            Score: {score}
          </Badge>
          <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30 px-4 py-2">
            Round: {round + 1}/{totalRounds}
          </Badge>
        </div>
        <Badge className="bg-red-500/20 text-red-400 border-red-500/30 px-4 py-2 flex items-center space-x-2">
          <Timer className="w-4 h-4" />
          <span>{timeLeft}s</span>
        </Badge>
      </div>
      
      {/* Reaction Area */}
      <Card className="gaming-card relative overflow-hidden">
        <button
          onClick={handleClick}
          disabled={gameState === 'finished'}
          className={`
            w-full h-96 lg:h-[400px] transition-all duration-200 flex flex-col items-center justify-center text-white font-bold text-2xl
            ${getBackgroundColor()}
            ${gameState === 'active' ? 'animate-pulse' : ''}
            hover:scale-105 active:scale-95
          `}
        >
          <div className="mb-4">
            {gameState === 'ready' && <Eye className="w-16 h-16" />}
            {gameState === 'active' && <Zap className="w-16 h-16 animate-bounce" />}
            {gameState === 'clicked' && !tooEarly && <Trophy className="w-16 h-16" />}
            {gameState === 'clicked' && tooEarly && <Eye className="w-16 h-16" />}
          </div>
          
          <div className="text-center">
            {getInstructionText()}
          </div>
        </button>
      </Card>
      
      {/* Reaction Times History */}
      {reactionTimes.length > 0 && (
        <div className="mt-6">
          <h3 className="text-white font-semibold mb-3 text-center">Your Reaction Times</h3>
          <div className="grid grid-cols-4 gap-2">
            {reactionTimes.map((time, index) => (
              <div 
                key={index}
                className={`
                  text-center p-2 rounded text-sm font-semibold
                  ${time < 200 ? 'bg-green-500/20 text-green-400' :
                    time < 300 ? 'bg-blue-500/20 text-blue-400' :
                    time < 400 ? 'bg-yellow-500/20 text-yellow-400' :
                    'bg-red-500/20 text-red-400'}
                `}
              >
                {time}ms
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Game Instructions */}
      <div className="mt-6 text-center text-gray-400 text-sm">
        {gameState === 'ready' ? 'Wait for the screen to turn green, then click as fast as possible!' :
         gameState === 'active' ? 'Click now! As fast as you can!' :
         'Get ready for the next round...'}
      </div>
    </div>
  );
};

export default ReactionTest;